import {
  Button,
  CheckboxField,
  Col,
  DropdownSubmitButton,
  FieldLabel,
  Form,
  MonetaryInputField,
  NumericFormatField,
  RemovableCard,
  Row,
  SelectField,
  SubmitButton,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import { MAX_PAYMENTS_WITH_COMMA } from '@/features/project/_utils/constants';
import { enableContractMethodDescription } from '@/features/project/_utils/payment';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import {
  ContractMethodLabelModifier,
  PaymentContractFormSchema,
  contractMethod as contractMethodModel,
  contractMethodOnContractor,
} from '@/features/project/projects/_models/payment-contract';
import { useForm } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { PlusOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = z.infer<typeof PaymentContractFormSchema>;
type CreatePaymentContractNextDrawerKey = 'payment-contract';

type Props = {
  projectId: string;
  initialValues?: Partial<FormValues>;
  paymentType: PaymentType;
  onSubmit: ({
    values,
    nextDrawerKey,
    onSuccess,
  }: {
    values: FormValues;
    nextDrawerKey?: CreatePaymentContractNextDrawerKey;
    onSuccess?: () => void;
  }) => Promise<void>;
};

export const PaymentContractForm: FC<Props> = ({
  projectId,
  initialValues,
  paymentType,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { drawerState } = useProjectDetailDrawerState();

  const project = useProject(projectId);
  const isKsStandardAfter2025 = useMemo(
    () =>
      project.data?.sheetType === 'KS' &&
      project.data.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project.data),
    [project],
  );

  const {
    handleSubmit,
    control,
    watch,
    resetField,
    reset,
    clearErrors,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(
      PaymentContractFormSchema.refine(
        (data) =>
          !(
            !isKsStandardAfter2025 &&
            !data.isOthers &&
            data.contractMethod &&
            [
              'open-tendering-lowest-price',
              'open-tendering-comprehensive-evaluation',
              'selective-tendering-lowest-price',
              'selective-tendering-comprehensive-evaluation',
              'negotiated-contract-competitive-bidding',
              'negotiated-contract-public-offering',
              'negotiated-contract-small-amount',
              'negotiated-contract-unsuccessful',
              'negotiated-contract-others',
              'subsidy',
            ].includes(data.contractMethod) &&
            typeof data.numberOfApplicants !== 'number'
          ),
        {
          path: ['numberOfApplicants'],
          message: t('validation.required'),
        },
      )
        .refine(
          (data) =>
            !(
              !isKsStandardAfter2025 &&
              !data.isOthers &&
              !data.hideBidRate &&
              data.contractMethod &&
              [
                'open-tendering-lowest-price',
                'open-tendering-comprehensive-evaluation',
                'selective-tendering-lowest-price',
                'selective-tendering-comprehensive-evaluation',
                'negotiated-contract-competitive-bidding',
                'negotiated-contract-public-offering',
                'negotiated-contract-unsuccessful',
                'negotiated-contract-others',
              ].includes(data.contractMethod) &&
              typeof data.bidRate !== 'number'
            ),
          {
            path: ['bidRate'],
            message: t('validation.required'),
          },
        )
        .refine(
          (data) =>
            !(
              !isKsStandardAfter2025 &&
              !data.isOthers &&
              data.hideBidRate &&
              !data.reason?.length
            ),
          {
            path: ['reason'],
            message: t('validation.required'),
          },
        ),
    ),
  });

  const {
    fields: amountBreakdownFields,
    append: appendAmountBreakdown,
    remove: removeAmountBreakdown,
  } = useFieldArray({
    control,
    name: 'amountBreakdown',
  });

  const [contractMethod, isOthers, hideBidRate] = watch([
    'contractMethod',
    'isOthers',
    'hideBidRate',
  ]);

  const isContractorPayment = paymentType === 'contractor';
  const isEditing =
    drawerState.open &&
    drawerState.type === 'payment-contract' &&
    drawerState.isEditing;

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit((values) => onSubmit({ values }))}
    >
      <TextAreaInputField
        name="overview"
        label={t('payment-contract-overview')}
        hint={
          project.data?.sheetType === 'SS'
            ? isContractorPayment
              ? t('hint-payment-contract-overview-for-contractor-for-ss')
              : t('hint-payment-contract-overview-for-ss')
            : isContractorPayment
              ? t('hint-payment-contract-overview-for-contractor')
              : t('hint-payment-contract-overview')
        }
        control={control}
        errors={errors}
        rows={3}
        required
      />

      <CheckboxField
        name="isOthers"
        text={t('is-other-payment-contract')}
        hint={t('hint-is-other-payment-contract')}
        control={control}
        errors={errors}
      />

      <MonetaryInputField
        name="amount"
        label={
          isContractorPayment
            ? t('contract-amount')
            : t('amount-of-expenditures')
        }
        hint={
          isContractorPayment ? undefined : t('hint-contractor-payment-amount')
        }
        control={control}
        errors={errors}
        unit={unit}
        required
      />

      <SelectField
        name="contractMethod"
        label={t('contract-method')}
        control={control}
        options={(isContractorPayment
          ? contractMethodOnContractor
          : contractMethodModel
        ).options.map((value) => ({
          value,
          label: ContractMethodLabelModifier[value],
        }))}
        errors={errors}
        required
        shouldCoexistOnChange
        onChange={(value) => {
          if (
            value === 'management-expense-grant' ||
            value === 'act-bearing-national-treasury-liabilities'
          ) {
            resetField('numberOfApplicants', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
            resetField('bidRate', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
          } else if (value === 'subsidy') {
            resetField('bidRate', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
          }
          if (!enableContractMethodDescription(value)) {
            resetField('contractMethodDescription', {
              keepDirty: false,
              keepTouched: false,
              keepError: false,
              defaultValue: null,
            });
          }
        }}
      />

      <TextInputField
        name="contractMethodDescription"
        label={t('contract-method-description')}
        hint={t('hint-contract-method-description')}
        control={control}
        errors={errors}
        disabled={!enableContractMethodDescription(contractMethod)}
        required={enableContractMethodDescription(contractMethod)}
      />

      <NumericFormatField
        disabled={
          isKsStandardAfter2025 ||
          (!!contractMethod &&
            (
              [
                'management-expense-grant',
                'act-bearing-national-treasury-liabilities',
              ] satisfies z.infer<typeof contractMethodModel>[]
            ).includes(contractMethod))
        }
        name="numberOfApplicants"
        label={t('number-of-applicants')}
        hint={
          isContractorPayment
            ? t('hint-contractor-number-of-applicants')
            : t('hint-number-of-applicants')
        }
        control={control}
        errors={errors}
        allowNegative={false}
        maxLength={MAX_PAYMENTS_WITH_COMMA}
        required={
          !isKsStandardAfter2025 &&
          !isOthers &&
          !!contractMethod &&
          (
            [
              'open-tendering-lowest-price',
              'open-tendering-comprehensive-evaluation',
              'selective-tendering-lowest-price',
              'selective-tendering-comprehensive-evaluation',
              'negotiated-contract-competitive-bidding',
              'negotiated-contract-public-offering',
              'negotiated-contract-small-amount',
              'negotiated-contract-unsuccessful',
              'negotiated-contract-others',
              'subsidy',
            ] satisfies z.infer<typeof contractMethodModel>[]
          ).includes(contractMethod)
        }
      />

      <CheckboxField
        name="hideBidRate"
        text={t('hide-bid-rate')}
        control={control}
        errors={errors}
        shouldCoexistOnChange
        onChange={() => {
          clearErrors(['bidRate', 'reason']);
        }}
      />

      <NumericFormatField
        disabled={
          isKsStandardAfter2025 ||
          (!!contractMethod &&
            (
              [
                'management-expense-grant',
                'act-bearing-national-treasury-liabilities',
                'subsidy',
              ] satisfies z.infer<typeof contractMethodModel>[]
            ).includes(contractMethod))
        }
        name="bidRate"
        label={t('bid-rate')}
        hint={
          isContractorPayment
            ? t('hint-contractor-bid-rate')
            : t('hint-bid-rate')
        }
        control={control}
        errors={errors}
        decimalScale={1}
        required={
          !isKsStandardAfter2025 &&
          !isOthers &&
          !hideBidRate &&
          !!contractMethod &&
          (
            [
              'open-tendering-lowest-price',
              'open-tendering-comprehensive-evaluation',
              'selective-tendering-lowest-price',
              'selective-tendering-comprehensive-evaluation',
              'negotiated-contract-competitive-bidding',
              'negotiated-contract-public-offering',
              'negotiated-contract-unsuccessful',
              'negotiated-contract-others',
            ] satisfies z.infer<typeof contractMethodModel>[]
          ).includes(contractMethod)
        }
      />

      <TextAreaInputField
        disabled={isKsStandardAfter2025}
        name="reason"
        label={t('payment-contract-reason')}
        hint={
          isKsStandardAfter2025
            ? undefined
            : isContractorPayment
              ? t('hint-payment-reason-for-contractor')
              : t('hint-payment-reason-for-payee')
        }
        control={control}
        errors={errors}
        rows={3}
        required={!isKsStandardAfter2025 && !isOthers && hideBidRate}
      />

      {!isContractorPayment && !isOthers && (
        <Form.Item
          label={
            <FieldLabel
              text={
                isKsStandardAfter2025
                  ? t(
                      'payment-amount-breakdown-expenditure-from-each-block-in-previous-year',
                    )
                  : t('payment-amount-breakdown')
              }
              hint={t('hint-payment-amount-breakdown')}
            />
          }
        >
          <Row gutter={[0, 8]}>
            {amountBreakdownFields.map((item, idx) => {
              return (
                <Col key={item.id} span={24}>
                  <RemovableCard
                    onRemove={() => {
                      removeAmountBreakdown(idx);
                    }}
                  >
                    <Row gutter={[8, 8]} wrap>
                      <Col span={24}>
                        <TextInputField
                          name={`amountBreakdown.${idx}.name`}
                          hint={t('hint-payment-amount-breakdown-name')}
                          autocompleteKey="amountBreakdown.name"
                          label={t('payment-amount-breakdown-name')}
                          control={control}
                          errors={errors}
                          required
                        />
                      </Col>
                      <Col span={24}>
                        <TextInputField
                          name={`amountBreakdown.${idx}.purpose`}
                          hint={t('hint-payment-amount-breakdown-purpose')}
                          autocompleteKey="amountBreakdown.purpose"
                          label={t('payment-amount-breakdown-purpose')}
                          control={control}
                          errors={errors}
                          required
                        />
                      </Col>
                      <Col span={24}>
                        <MonetaryInputField
                          key={amountBreakdownFields[idx]?.amount}
                          name={`amountBreakdown.${idx}.amount`}
                          label={t('payment-amount-breakdown-amount')}
                          control={control}
                          errors={errors}
                          unit={unit}
                        />
                      </Col>
                    </Row>
                  </RemovableCard>
                </Col>
              );
            })}
            <Button
              type="dashed"
              onClick={() => {
                appendAmountBreakdown({
                  name: '',
                  purpose: '',
                  amount: null,
                });
              }}
              block
              icon={<PlusOutlined />}
            >
              {t('add-payment-amount-breakdown')}
            </Button>
          </Row>
        </Form.Item>
      )}

      {isEditing ? (
        <SubmitButton loading={isSubmitting} disabled={!isDirty} />
      ) : (
        <DropdownSubmitButton<CreatePaymentContractNextDrawerKey>
          loading={isSubmitting}
          menuItems={[
            {
              key: 'payment-contract',
              label: t('save-and-create-next-item', {
                item: t('payment-contract'),
              }),
            },
          ]}
          onClick={(e) => {
            handleSubmit(async (values) => {
              await onSubmit({
                values,
                nextDrawerKey: e.key,
                onSuccess: () => {
                  reset(initialValues);
                },
              });
            })();
          }}
        />
      )}
    </Form>
  );
};
